import React, { useState, useEffect, useRef } from "react";
import { collection, query, onSnapshot, orderBy, limit } from "firebase/firestore";
import { db } from "../../util/firebase";
import { executeApi } from "../../util/apiHelper";

import { FiBell, FiExternalLink, FiEye, FiCheck, FiX, FiCheckCircle } from "react-icons/fi";
import { RiAlarmWarningLine } from "react-icons/ri";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { useNavigate } from "react-router-dom";
import Context from "../../Context";

export const AdminAlertBell = () => {
  const [alerts, setAlerts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSlipModal, setSelectedSlipModal] = useState(null);
  const unsubRef = useRef(null);
  const navigate = useNavigate();
  const { callApi } = Context();

  useEffect(() => {
    if (!db) return;
    try {
      const q = query(
        collection(db, "AdminAlerts"),
        orderBy("createdAt", "desc"),
        limit(20)
      );

      unsubRef.current = onSnapshot(
        q,
        (snap) => {
          const list = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          setAlerts(list);
        },
        (err) => {
          console.warn("AdminAlertBell snapshot warning:", err?.message);
        }
      );
    } catch (e) {
      console.warn("Failed to listen to AdminAlerts:", e);
    }

    return () => {
      if (unsubRef.current) unsubRef.current();
    };
  }, []);

  const unreadAlerts = alerts.filter((a) => a.status === "unread");
  const unreadCount = unreadAlerts.length;
  const hasDanger = unreadAlerts.some((a) => a.level === "danger" || a.type === "DUPLICATE_SLIP");

  const handleResolveAlert = async (e, alertId) => {
    e.stopPropagation();
    if (!alertId) return;
    // Optimistic UI update
    setAlerts((prev) =>
      prev.map((a) =>
        a.id === alertId ? { ...a, status: "resolved", resolvedAt: Date.now() } : a
      )
    );
    try {
      if (callApi) {
        await callApi("utils-resolveAdminAlert", { alertId }, { notShowLoading: true });
      } else {
        await executeApi("utils-resolveAdminAlert", { alertId });
      }
    } catch (err) {
      console.error("Failed to resolve alert:", err);
    }
  };

  const handleResolveAll = async (e) => {
    e.stopPropagation();
    if (unreadAlerts.length === 0) return;
    const ids = unreadAlerts.map((a) => a.id);
    // Optimistic UI update
    setAlerts((prev) =>
      prev.map((a) =>
        ids.includes(a.id) ? { ...a, status: "resolved", resolvedAt: Date.now() } : a
      )
    );
    try {
      if (callApi) {
        await callApi("utils-resolveAdminAlert", { alertIds: ids }, { notShowLoading: true });
      } else {
        await executeApi("utils-resolveAdminAlert", { alertIds: ids });
      }
    } catch (err) {
      console.error("Failed to resolve all alerts:", err);
    }

  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="relative h-12 w-12 bg-white text-gray-700 hover:text-gray-900 rounded-full shadow-md hover:shadow-xl duration-150 flex items-center justify-center border border-gray-100 transition focus:outline-none"
            title="การแจ้งเตือน"
          >
            <FiBell className={`text-xl ${unreadCount > 0 ? (hasDanger ? "text-[#c15b78] animate-bounce" : "text-[#FAB12F]") : "text-gray-600"}`} />

            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 min-w-5 px-1 items-center justify-center bg-[#c15b78] text-white text-[10px] font-extrabold rounded-full border-2 border-white shadow-xs">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-80 sm:w-96 p-0 bg-white rounded-md shadow-2xl border border-gray-200 z-50 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 border-b border-gray-200">
            <div className="flex items-center gap-1.5">
              <RiAlarmWarningLine className="text-[#c15b78] text-lg" />
              <div>
                <h4 className="font-bold text-xs text-gray-900 leading-none">
                  แจ้งเตือน
                </h4>
                {/* <span className="text-[10px] text-gray-500 font-medium">
                  {unreadCount > 0 ? `พบ ${unreadCount} รายการใหม่` : "ระบบทำงานปกติ"}
                </span> */}
              </div>
            </div>

            {unreadCount > 0 && (
              <button
                onClick={handleResolveAll}
                className="text-[10px] font-bold text-blue-600 hover:text-blue-800 transition px-2 py-0.5 rounded-md hover:bg-blue-50"
              >
                เคลียร์ทั้งหมด
              </button>
            )}
          </div>

          {/* Alert List */}
          <div className="max-h-[380px] overflow-y-auto divide-y divide-gray-100">
            {alerts.length === 0 ? (
              <div className="py-8 px-4 flex flex-col items-center justify-center text-center text-gray-400">
                <FiCheckCircle className="text-3xl text-[rgb(59,176,101)] mb-1.5 opacity-80" />
                <p className="text-xs font-semibold text-gray-700">ไม่มีรายการแจ้งเตือน</p>
                <span className="text-[10px] text-gray-400">ระบบตรวจสลิปทำงานราบรื่น</span>
              </div>
            ) : (
              alerts.map((alert) => {
                const isDanger = alert.level === "danger" || alert.type === "DUPLICATE_SLIP";
                const isWarning = alert.level === "warning" || alert.type === "NO_SLIP_ID";
                const isResolved = alert.status === "resolved";

                return (
                  <div
                    key={alert.id}
                    className={`p-3 transition hover:bg-slate-50/80 flex flex-col gap-1.5 ${isResolved
                      ? "opacity-60 bg-gray-50/50 border-l-4 border-gray-300"
                      : isDanger
                        ? "bg-[#c15b78]/5 border-l-4 border-[#c15b78]"
                        : isWarning
                          ? "bg-[#FAB12F]/5 border-l-4 border-[#FAB12F]"
                          : "bg-[#4DA1A9]/5 border-l-4 border-[#4DA1A9]"
                      }`}
                  >
                    {/* Top line: Badges & Time */}
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span
                          className={`text-[9px] font-black uppercase px-1.5 py-0.2 rounded-md ${isDanger
                            ? "bg-[#c15b78] text-white"
                            : isWarning
                              ? "bg-[#FAB12F] text-white"
                              : "bg-[#4DA1A9] text-white"
                            }`}
                        >
                          {isDanger ? "สลิปซ้ำ" : isWarning ? "ไม่พบ QR" : "แจ้งเตือน"}
                        </span>
                        <span className="font-mono text-xs font-bold text-blue-600">
                          #{alert.shortId || alert.orderId?.slice(-6)}
                        </span>
                      </div>
                      <span className="text-[9px] text-gray-400 font-medium">
                        {alert.createdAt
                          ? new Date(alert.createdAt).toLocaleTimeString("th-TH", {
                            hour: "2-digit",
                            minute: "2-digit",
                          }) + " น."
                          : "-"}
                      </span>
                    </div>

                    {/* Description message */}
                    <p className="text-[11px] font-semibold text-gray-800 leading-snug">
                      {alert.message || alert.title}
                    </p>

                    {/* Customer Info */}
                    <div className="text-[10px] text-gray-500 flex items-center gap-2 flex-wrap">
                      {alert.customerName && alert.customerName !== "-" && (
                        <span>ลูกค้า: <strong className="text-gray-700">{alert.customerName}</strong></span>
                      )}
                      {alert.phone && alert.phone !== "-" && (
                        <span>โทร: <strong className="text-gray-700">{alert.phone}</strong></span>
                      )}
                      {alert.totalPrice > 0 && (
                        <span>ยอด: <strong className="text-emerald-700">฿{Number(alert.totalPrice).toLocaleString()}</strong></span>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center justify-between pt-1 border-t border-black/5 mt-0.5">
                      <div className="flex items-center gap-1">
                        {alert.slipUrl && (
                          <button
                            onClick={() => setSelectedSlipModal(alert)}
                            className="flex items-center gap-0.5 bg-white hover:bg-slate-100 text-gray-700 text-[10px] font-bold px-2 py-0.5 rounded-md border border-gray-200 transition shadow-2xs"
                          >
                            <FiEye className="text-blue-600" />
                            <span>ดูสลิป</span>
                          </button>
                        )}
                        <button
                          onClick={() => window.open(`/orders/${alert.orderId}`, "_blank")}
                          className="flex items-center gap-0.5 bg-white hover:bg-slate-100 text-blue-600 text-[10px] font-bold px-2 py-0.5 rounded-md border border-blue-200 transition shadow-2xs"
                        >
                          <FiExternalLink />
                          <span>เปิดออเดอร์</span>
                        </button>
                      </div>

                      {!isResolved && (
                        <button
                          onClick={(e) => handleResolveAlert(e, alert.id)}
                          className="flex items-center gap-0.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-md border border-emerald-200 transition shadow-2xs"
                          title="เคลียร์แจ้งเตือนนี้"
                        >
                          <FiCheck />
                          <span>เคลียร์</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer link to Dashboard */}
          {/* <div className="p-2 bg-slate-50 border-t border-gray-200 text-center">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate("/dashboard");
              }}
              className="text-[11px] font-bold text-gray-700 hover:text-blue-600 transition inline-flex items-center gap-1"
            >
              <span>เปิดดูทั้งหมดในแดชบอร์ด</span>
              <FiExternalLink className="text-[10px]" />
            </button>
          </div> */}
        </PopoverContent>
      </Popover>

      {/* Slip Image Preview Modal */}
      {selectedSlipModal && (
        <div
          className="fixed inset-0 z-[9999] bg-black/75 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setSelectedSlipModal(null)}
        >
          <div
            className="bg-white rounded-md p-4 max-w-md w-full shadow-2xl border border-gray-200 max-h-[90vh] flex flex-col justify-between"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-3">
              <div>
                <h4 className="font-bold text-sm text-gray-900 flex items-center gap-1.5">
                  <span>สลิปคำสั่งซื้อ</span>
                  <span className="font-mono text-blue-600 font-bold">
                    #{selectedSlipModal.shortId || selectedSlipModal.orderId?.slice(-6)}
                  </span>
                </h4>
                <p className="text-[10px] text-gray-500">
                  {selectedSlipModal.customerName} ({selectedSlipModal.phone || "-"})
                </p>
              </div>
              <button
                onClick={() => setSelectedSlipModal(null)}
                className="p-1 rounded-md hover:bg-gray-100 text-gray-500 transition"
              >
                <FiX className="text-lg" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto flex items-center justify-center bg-gray-50 rounded-md p-2 border border-gray-100 mb-3">
              <img
                src={selectedSlipModal.slipUrl}
                alt="Slip Preview"
                className="max-h-[55vh] object-contain rounded-md shadow-sm"
              />
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-100 text-xs">
              <button
                onClick={() => window.open(selectedSlipModal.slipUrl, "_blank")}
                className="text-gray-600 hover:text-gray-900 font-semibold text-[11px] underline"
              >
                เปิดรูปขนาดเต็ม
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    window.open(`/orders/${selectedSlipModal.orderId}`, "_blank");
                    setSelectedSlipModal(null);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-3 py-1.5 rounded-md text-xs transition shadow-2xs flex items-center gap-1"
                >
                  <FiExternalLink />
                  <span>ไปที่คำสั่งซื้อ</span>
                </button>
                <button
                  onClick={() => setSelectedSlipModal(null)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-3 py-1.5 rounded-md text-xs transition"
                >
                  ปิด
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminAlertBell;
